import { NativeEventEmitter, type EmitterSubscription } from "react-native";

import TypedNativeModules from "../internal/TypedNativeModules";
import type {
  BroadcastEvent,
  BroadcastEventMap,
  BroadcastEventName,
} from "../types/RNBroadcastModule";

const RNBroadcastModule = TypedNativeModules.RNBroadcastModule;

let eventEmitter: NativeEventEmitter | null = null;

function getEventEmitter(): NativeEventEmitter | null {
  if (!RNBroadcastModule) {
    return null;
  }
  if (!eventEmitter) {
    eventEmitter = new NativeEventEmitter(RNBroadcastModule);
  }
  return eventEmitter;
}

export interface JstBroadcastSubscription {
  /**
   * Unsubscribe from the event.
   */
  remove: () => Promise<void>;
}

/**
 * Subscribe to a JSTBroadcast event.
 *
 * @param eventType - The type of event to subscribe to
 * @param callback - The callback function to invoke when the event is received
 * @returns A subscription object with a remove method to unsubscribe
 *
 * @example
 * ```typescript
 * const subscription = await jstBroadcast.subscribe('ViewWillAppear', (event) => {
 *     console.log('View will appear:', event);
 * });
 *
 * // Later, unsubscribe
 * await subscription.remove();
 * ```
 */
export async function subscribe<T extends BroadcastEventName>(
  eventType: T,
  callback: (event: BroadcastEventMap[T]) => void,
): Promise<JstBroadcastSubscription | null> {
  if (!RNBroadcastModule) {
    return null;
  }

  const emitter = getEventEmitter();
  if (!emitter) {
    return null;
  }

  // Add event listener
  const subscription: EmitterSubscription = emitter.addListener(eventType, callback);

  // Subscribe to the native module to get a handle
  let handle: string | null = null;
  try {
    handle = await RNBroadcastModule.subscribe(eventType);
  } catch (error) {
    console.error(`Failed to subscribe to JSTBroadcast for event type: ${eventType}`, error);
    subscription.remove();
    return null;
  }

  return {
    remove: async () => {
      subscription.remove();
      try {
        await RNBroadcastModule.unsubscribe(handle);
      } catch (error) {
        console.error("Failed to unsubscribe from JSTBroadcast", error);
      }
    },
  };
}

/**
 * Dispatch a JSTBroadcast event.
 *
 * @param event - The broadcast event to dispatch
 * @returns A promise that resolves when the event has been dispatched
 *
 * @example
 * ```typescript
 * await jstBroadcast.dispatch({
 *     eventType: 'ViewWillAppear',
 *     data: { screenName: 'Home' },
 * });
 * ```
 */
export async function dispatch(event: BroadcastEvent): Promise<void> {
  if (!RNBroadcastModule) {
    return;
  }

  await RNBroadcastModule.dispatch(event);
}
